
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface VariantMeasurement {
  id: string;
  part_name: string;
  measurement_value: number;
  tolerance: number | null;
  notes: string | null;
  description: string | null;
  products: string[]; // Store product names as strings
  factories: string[]; // Store factory names as strings
  is_required: boolean;
  measurement_order: number;
  created_at: string;
  updated_at: string;
}

export interface VariantMeasurementFormValues {
  part_name: string;
  measurement_value: number;
  tolerance?: number | null;
  notes?: string | null;
  description?: string | null;
  products: string[]; // Store product names as strings
  factories: string[]; // Store factory names as strings
  is_required: boolean;
  measurement_order: number;
}

export function useVariantMeasurements() {
  const [variantMeasurements, setVariantMeasurements] = useState<VariantMeasurement[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchVariantMeasurements = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('variant_measurements')
        .select('*')
        .order('measurement_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching variant measurements:", error);
        toast.error("Failed to load variant measurements");
        return false;
      }

      // Transform the data to ensure products and factories are arrays of strings
      const transformedData = data?.map(item => ({
        ...item,
        // Ensure these are arrays of strings (names)
        products: Array.isArray(item.products) ? item.products : [],
        factories: Array.isArray(item.factories) ? item.factories : [],
        is_required: item.is_required || false
      }));

      setVariantMeasurements(transformedData as VariantMeasurement[]);
      return true;
    } catch (error: any) {
      console.error("Error in fetchVariantMeasurements:", error.message);
      toast.error("Failed to load variant measurements");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createVariantMeasurement = async (values: VariantMeasurementFormValues) => {
    try {
      const { data, error } = await supabase
        .from('variant_measurements')
        .insert([values])
        .select()
        .single();

      if (error) {
        console.error("Error creating variant measurement:", error);
        toast.error("Failed to create variant measurement");
        return false;
      }

      toast.success("Variant measurement created successfully");
      return true;
    } catch (error: any) {
      console.error("Error in createVariantMeasurement:", error.message);
      toast.error("Failed to create variant measurement");
      return false;
    }
  };

  const updateVariantMeasurement = async (id: string, values: VariantMeasurementFormValues) => {
    try {
      const { error } = await supabase
        .from('variant_measurements')
        .update(values)
        .eq('id', id);

      if (error) {
        console.error("Error updating variant measurement:", error);
        toast.error("Failed to update variant measurement");
        return false;
      }

      toast.success("Variant measurement updated successfully");
      return true;
    } catch (error: any) {
      console.error("Error in updateVariantMeasurement:", error.message);
      toast.error("Failed to update variant measurement");
      return false;
    }
  };

  const deleteVariantMeasurement = async (measurement: VariantMeasurement) => {
    try {
      const { error } = await supabase
        .from('variant_measurements')
        .delete()
        .eq('id', measurement.id);

      if (error) {
        console.error("Error deleting variant measurement:", error);
        toast.error("Failed to delete variant measurement");
        return false;
      }

      toast.success("Variant measurement deleted successfully");
      return true;
    } catch (error: any) {
      console.error("Error in deleteVariantMeasurement:", error.message);
      toast.error("Failed to delete variant measurement");
      return false;
    }
  };

  return {
    variantMeasurements,
    loading,
    fetchVariantMeasurements,
    createVariantMeasurement,
    updateVariantMeasurement,
    deleteVariantMeasurement
  };
}
